import React, { useState, useEffect, useCallback } from 'react';
import { companionService, Companion } from '../../services/companionService';
import { useAuth } from '../../contexts/AuthContext';
import TopBar from '../TopBar';
import LoadingScreen from '../ui/LoadingScreen';

import { preloadService } from '../../services/preloadService';
import OptimizedImage from '../ui/OptimizedImage';

// CharacterStats interface removed as it's not used

const Character: React.FC = () => {
  const [characters, setCharacters] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();

  const loadCharacters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await companionService.getAllCompanions();
      setCharacters(response.companions);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load characters:', err);
      setCharacters([]);
      
      // Handle network/CORS errors gracefully
      if (err.message && err.message.includes('Network error')) {
        console.log('Network/CORS error - continuing with empty state');
        setError(null);
      } else if (err?.status === 403 || err?.status === 401) {
        setError(null);
      } else {
        setError('Failed to load characters. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters]);

  const handleCharacterClick = (character: Companion) => {
    // Navigate to chat with this character
    console.log('Selected character:', typeof character.name === 'object' && character.name
      ? (character.name.nick_name || `${character.name.first_name || ''} ${character.name.last_name || ''}`.trim() || 'Unknown')
      : String(character.name || 'Unknown'));
    // You can add navigation logic here
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900">
        <TopBar />
        <div className="flex justify-center items-center h-96 pt-24">
          <LoadingScreen
            title="Loading Characters"
            message="Fetching character data..."
            variant="inline"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900">
      <TopBar />
      
      <div className="container mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            Discover <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">AI Characters</span>
          </h1>
          <p className="text-xl text-dark-300 max-w-3xl mx-auto mb-8">
            Explore amazing AI personalities created by our community. Find your perfect companion and start meaningful conversations.
          </p>
          
          {/* Create New Character Button */}
          <a
            href="/create"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
          >
            <span className="text-xl">✨</span>
            Create New Character
          </a>
        </div>

        {error && (
          <div className="glass-effect rounded-2xl p-8 border border-dark-600 text-center mb-8">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-white mb-2">Connection Error</h3>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={loadCharacters}
              className="px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Characters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {characters.map((character, index) => {
            // Handle new assets structure
            let avatar = '';
            if (character.assets && typeof character.assets === 'object' && !Array.isArray(character.assets)) {
              // New structure
              avatar = character.assets.primary_photo?.presigned_url || character.assets.primary_video?.presigned_url || '';
            } else {
              // Legacy structure
              const images = (character as any).images || [];
              const primaryImage = images.find((img: any) => img.is_primary) || images[0];
              avatar = primaryImage?.image_url || (character as any).avatar_url || '';
            }

            return (
              <div
                key={(character as any).companion_id || 'character'}
                onClick={() => handleCharacterClick(character)}
                onMouseEnter={() => {
                  // Preload character images on hover
                  preloadService.preloadCompanionImages((character as any).companion_id || 'character');
                }}
                className="group cursor-pointer transform hover:scale-[1.02] transition-all duration-300 hover:shadow-xl"
              >
                {/* Character Card */}
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-dark-800 shadow-lg">
                  {avatar ? (
                    <OptimizedImage
                      src={avatar}
                      alt={typeof character.name === 'object' && character.name
                        ? (character.name.nick_name || `${character.name.first_name || ''} ${character.name.last_name || ''}`.trim() || 'Unknown')
                        : String(character.name || 'Unknown')}
                      className="w-full h-full"
                      placeholder="👤"
                      priority={index < 6}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
                      <span className="text-6xl">✨</span>
                    </div>
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                  {/* Character Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg mb-2">
                      {typeof character.name === 'object' && character.name
                        ? (character.name.nick_name || `${character.name.first_name || ''} ${character.name.last_name || ''}`.trim() || 'Unknown')
                        : String(character.name || 'Unknown')}
                    </h3>
                    
                    {/* Age */}
                    {character.age && (
                      <div className="text-white/80 text-sm mb-2">
                        Age: {character.age}
                      </div>
                    )}

                    {/* Personality/Specialties */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {character.categories && character.categories.slice(0, 2).map((specialty, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-accent-purple/80 text-white text-xs rounded-full font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                      {!character.categories?.length && character.personality && (
                        <span className="px-2 py-1 bg-accent-purple/80 text-white text-xs rounded-full font-medium">
                          {typeof character.personality === 'string' 
                            ? character.personality 
                            : JSON.stringify(character.personality)
                          }
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {character.description && (
                      <p className="text-white/90 text-sm leading-relaxed mb-3 overflow-hidden" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as const
                      }}>
                        {character.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-white/80 text-sm">
                        <span className="flex items-center gap-1">
                          <span className="text-red-400">❤️</span>
                          {0}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-blue-400">💬</span>
                          {0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-accent-purple/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {characters.length === 0 && !loading && !error && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">✨</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              No Characters Available
            </h3>
            <p className="text-dark-300 mb-6">
              Be the first to create an amazing AI character!
            </p>
            <a
              href="/create"
              className="inline-block px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
            >
              Create Character
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Character;